import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button'
import GlassCard from '@/components/GlassCard'
import QrCode from '@/components/QrCode'
import type { ObstacleKind, RunStats } from '@/game/types'
import { getLang } from '@/i18n'
import { submitLead } from '@/lib/api'
import type { ScoreEntry } from '@/lib/leaderboard'

import styles from './gameOver.module.scss'

export interface GameOverScreenProps {
  name: string
  score: number
  code: string
  stats: RunStats
  crashKind: ObstacleKind | null
  rank: number
  isPersonalBest: boolean
  topToday: ScoreEntry[]
  onRestart: () => void
  onHome: () => void
}

const DEMO_URL =
  'https://onitsoft.com/contact-us?utm_source=targi&utm_medium=game&utm_campaign=trucking-game'

const EMAIL_RE = /^\S+@\S+\.\S+$/
const BOARD_ROWS = 5

type LeadStatus = 'idle' | 'sending' | 'sent' | 'queued' | 'error'
type LeadError = 'invalidEmail' | 'consentRequired' | null

/** `1 234` with a narrow no-break space. */
function groupDigits(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')
}

function formatTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const mm = Math.floor(total / 60)
  const ss = total % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

export default function GameOverScreen({
  name,
  score,
  code,
  stats,
  crashKind,
  rank,
  isPersonalBest,
  topToday,
  onRestart,
  onHome,
}: GameOverScreenProps) {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [consent, setConsent] = useState(false)
  const [status, setStatus] = useState<LeadStatus>('idle')
  const [leadError, setLeadError] = useState<LeadError>(null)

  const sendLead = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (status === 'sending') return

    const trimmedEmail = email.trim()
    if (!EMAIL_RE.test(trimmedEmail)) {
      setLeadError('invalidEmail')
      return
    }
    if (!consent) {
      setLeadError('consentRequired')
      return
    }

    setLeadError(null)
    setStatus('sending')
    const trimmedPhone = phone.trim()
    submitLead({
      name,
      email: trimmedEmail,
      phone: trimmedPhone === '' ? undefined : trimmedPhone,
      score,
      code,
      lang: getLang(),
    })
      .then((result) => setStatus(result))
      .catch(() => setStatus('error'))
  }

  const statItems: readonly { key: string; value: string }[] = [
    { key: 'over.stats.orders', value: String(stats.orders) },
    { key: 'over.stats.fuel', value: String(stats.fuel) },
    { key: 'over.stats.bobi', value: String(stats.bobi) },
    { key: 'over.stats.sokar', value: String(stats.sokar) },
    { key: 'over.stats.near', value: String(stats.nearMisses) },
    { key: 'over.stats.distance', value: `${groupDigits(stats.distance)}\u00a0m` },
    { key: 'over.stats.time', value: formatTime(stats.time) },
    { key: 'over.stats.topSpeed', value: `${Math.round(stats.topSpeed)}\u00a0km/h` },
  ]

  const board = topToday.slice(0, BOARD_ROWS)
  const settled = status === 'sent' || status === 'queued'

  return (
    <div className={styles.screen}>
      <div className={styles.scroll}>
        <header className={styles.rise}>
          <h1 className={styles.headline}>
            {isPersonalBest ? t('over.titleBest', { name }) : t('over.title', { name })}
          </h1>
          {isPersonalBest && <span className={styles.pbBadge}>{t('over.personalBest')}</span>}
        </header>

        <GlassCard className={`${styles.scoreBlock} ${styles.rise} ${styles.d1}`}>
          <strong className={styles.scoreValue}>{groupDigits(score)}</strong>
          <span className={styles.scoreLabel}>{t('over.scoreLabel')}</span>
          <span className={styles.rankLabel}>{t('over.rankLabel', { rank })}</span>
        </GlassCard>

        {crashKind !== null && (
          <GlassCard className={`${styles.crashCard} ${styles.rise} ${styles.d2}`}>
            <span className={styles.crashTitle}>
              {t('over.crashedBy', { obstacle: t(`entity.${crashKind}.name`) })}
            </span>
            <p className={styles.crashPitch}>{t(`entity.${crashKind}.hit`)}</p>
          </GlassCard>
        )}

        <div className={styles.stats}>
          {statItems.map((item) => (
            <div
              key={item.key}
              className={styles.stat}
            >
              <span className={styles.statLabel}>{t(item.key)}</span>
              <span className={styles.statValue}>{item.value}</span>
            </div>
          ))}
        </div>

        <GlassCard className={styles.card}>
          <h2 className={styles.cardTitle}>{t('over.prize.title')}</h2>
          <p className={styles.cardText}>{t('over.prize.desc')}</p>
          <div className={styles.codePanel}>
            <span className={styles.codeLabel}>{t('over.codeLabel')}</span>
            <strong className={styles.codeValue}>{code}</strong>
          </div>
          <p className={styles.codeHint}>{t('over.codeHint')}</p>
        </GlassCard>

        {board.length > 0 && (
          <GlassCard className={styles.card}>
            <h2 className={styles.cardTitle}>{t('common.today')}</h2>
            <ol className={styles.board}>
              {board.map((entry, index) => (
                <li
                  key={`${entry.code}-${entry.ts}`}
                  className={
                    entry.code === code ?
                      `${styles.boardRow} ${styles.boardRowMe}`
                    : styles.boardRow
                  }
                >
                  <span className={styles.boardRank}>#{index + 1}</span>
                  <span className={styles.boardName}>
                    {entry.name}
                    {entry.code === code && (
                      <span className={styles.youTag}>{t('common.you')}</span>
                    )}
                  </span>
                  <span className={styles.boardScore}>{groupDigits(entry.score)}</span>
                </li>
              ))}
            </ol>
          </GlassCard>
        )}

        <GlassCard className={styles.card}>
          <h2 className={styles.cardTitle}>{t('over.demo.title')}</h2>
          <p className={styles.cardText}>{t('over.demo.desc')}</p>
          <div className={styles.qrWrap}>
            <QrCode value={DEMO_URL} />
          </div>
          <p className={styles.qrHint}>{t('over.demo.qrHint')}</p>
          <Button
            block
            onClick={() => window.open(DEMO_URL, '_blank', 'noopener,noreferrer')}
          >
            {t('over.demo.button')}
          </Button>
        </GlassCard>

        <GlassCard className={styles.card}>
          <h2 className={styles.cardTitle}>{t('over.lead.title')}</h2>
          {settled ?
            <p className={styles.done}>
              {status === 'sent' ? t('over.lead.sent') : t('over.lead.queued')}
            </p>
          : <>
              <p className={styles.cardText}>{t('over.lead.desc')}</p>
              <form
                className={styles.leadForm}
                onSubmit={sendLead}
                noValidate
              >
                <div className={styles.field}>
                  <label
                    className={styles.label}
                    htmlFor='lead-email'
                  >
                    {t('over.lead.email')}
                  </label>
                  <input
                    id='lead-email'
                    className={
                      leadError === 'invalidEmail' ?
                        `${styles.input} ${styles.inputError}`
                      : styles.input
                    }
                    type='email'
                    inputMode='email'
                    autoComplete='email'
                    enterKeyHint='next'
                    value={email}
                    placeholder={t('over.lead.emailPlaceholder')}
                    aria-invalid={leadError === 'invalidEmail'}
                    onChange={(event) => {
                      setEmail(event.target.value)
                      if (leadError === 'invalidEmail') setLeadError(null)
                    }}
                  />
                </div>

                <div className={styles.field}>
                  <label
                    className={styles.label}
                    htmlFor='lead-phone'
                  >
                    {t('over.lead.phone')}
                  </label>
                  <input
                    id='lead-phone'
                    className={styles.input}
                    type='tel'
                    inputMode='tel'
                    autoComplete='tel'
                    enterKeyHint='done'
                    value={phone}
                    placeholder={t('over.lead.phonePlaceholder')}
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </div>

                <label className={styles.consent}>
                  <input
                    className={styles.checkbox}
                    type='checkbox'
                    checked={consent}
                    onChange={(event) => {
                      setConsent(event.target.checked)
                      if (leadError === 'consentRequired') setLeadError(null)
                    }}
                  />
                  {t('over.lead.consent')}
                </label>

                {leadError !== null && (
                  <p className={styles.formError}>{t(`over.lead.${leadError}`)}</p>
                )}
                {status === 'error' && <p className={styles.formError}>{t('over.lead.error')}</p>}

                <Button
                  type='submit'
                  block
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? t('over.lead.sending') : t('over.lead.submit')}
                </Button>
              </form>
            </>
          }
        </GlassCard>
      </div>

      <div className={styles.actions}>
        <Button
          className={styles.actionMain}
          onClick={onRestart}
        >
          {t('common.playAgain')}
        </Button>
        <Button
          variant='ghost'
          className={styles.actionSide}
          onClick={onHome}
        >
          {t('common.home')}
        </Button>
      </div>
    </div>
  )
}
